import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { api, trackEvent } from "@/lib/api";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useSite } from "@/context/SiteContext";
import { useLang } from "@/context/LanguageContext";
import { IMAGES, LOGO } from "@/lib/site";
import { MarketTicker } from "@/components/common/MarketTicker";
import { SectionHeading } from "@/components/common/SectionHeading";
import { MarketCard } from "@/components/common/MarketCard";
import { FeatureCard } from "@/components/common/FeatureCard";
import { BrokerCard } from "@/components/common/BrokerCard";
import { ArticleCard } from "@/components/common/ArticleCard";
import { EducationCard } from "@/components/common/EducationCard";
import { HowItWorks } from "@/components/common/HowItWorks";
import { CTASection } from "@/components/common/CTASection";
import { FAQAccordion } from "@/components/common/FAQAccordion";
import { Newsletter } from "@/components/common/Newsletter";
import { StatStrip } from "@/components/common/StatStrip";
import {
  ArrowRight, CircleDollarSign, Boxes, BarChart3, Building, ShieldCheck, Headphones,
  Gauge, LineChart, MonitorSmartphone, Globe2, Monitor, Layers, Smartphone, Globe,
} from "lucide-react";
import { HOME_FAQ, HOME_FAQ_AR } from "@/lib/content";

const MARKET_ICONS = [CircleDollarSign, Boxes, BarChart3, Building];
const MARKET_LINKS = ["/markets/forex", "/markets/commodities", "/markets/indices", "/markets/shares"];
const BENEFIT_ICONS = [ShieldCheck, Headphones, Gauge, LineChart, MonitorSmartphone, Globe2];
const PLATFORM_ICONS = [Monitor, Layers, Globe, Smartphone];

export default function Home() {
  const { settings } = useSite();
  const { L, isAr } = useLang();
  usePageSeo("home", {
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "MENAGroupFX",
          url: "https://menagroupfx.com",
          logo: LOGO,
          description: "MENAGroupFX is an Introducing Broker connecting traders with regulated brokerage partners across global financial markets.",
        },
        {
          "@type": "WebSite",
          name: "MENAGroupFX",
          url: "https://menagroupfx.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://menagroupfx.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        },
      ],
    },
  });

  const { data: brokers } = useQuery({ queryKey: ["brokers"], queryFn: async () => (await api.get("/brokers")).data });
  const { data: articles } = useQuery({ queryKey: ["home-articles"], queryFn: async () => (await api.get("/articles?page=1&page_size=3")).data });
  const { data: education } = useQuery({ queryKey: ["home-edu"], queryFn: async () => (await api.get("/education")).data });

  return (
    <>
      <section className="relative overflow-hidden bg-navy">
        <div className="absolute inset-0">
          <img src={IMAGES.skyline} alt="Global financial district" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70 rtl:bg-gradient-to-l" />
          <div className="absolute inset-0 grid-lines opacity-40" />
        </div>
        <div className="container-tight relative py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white/5 px-3 py-1 text-xs font-semibold text-gold">
              <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {L.hero.badge}
            </span>
            <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">{L.hero.title}</h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">{L.hero.desc}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-finblue hover:bg-finblueDark" onClick={() => trackEvent("open_account_click", { location: "hero" })} data-testid="hero-open-account">
                <Link to="/broker-partners">{L.common.openAnAccount} <ArrowRight className="h-4 w-4 rtl:rotate-180" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="hero-explore-partners">
                <Link to="/broker-partners">{L.common.explorePartners}</Link>
              </Button>
            </div>
            <p className="mt-6 max-w-lg text-xs leading-relaxed text-slate-400">{L.hero.disclosure}</p>
          </div>
        </div>
      </section>

      <MarketTicker />

      <section className="py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading overline={L.marketAccess.overline} title={L.marketAccess.title} description={L.marketAccess.desc} />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {L.marketAccess.cards.map((c, i) => (
              <MarketCard key={i} icon={MARKET_ICONS[i]} title={c[0]} description={c[1]} cta={c[2]} to={MARKET_LINKS[i]} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-canvas py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading overline={L.why.overline} title={L.why.title} description={L.why.desc} />
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {L.why.benefits.map((b, i) => <FeatureCard key={i} icon={BENEFIT_ICONS[i]} title={b[0]} description={b[1]} />)}
          </div>
          {settings.trust_metrics?.length > 0 && <div className="mt-12"><StatStrip metrics={settings.trust_metrics} /></div>}
        </div>
      </section>

      <HowItWorks />

      <section className="py-20 lg:py-28">
        <div className="container-tight">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading overline={L.brokers.overline} title={L.brokers.title} />
            <Button asChild variant="outline" data-testid="home-view-brokers"><Link to="/broker-partners">{L.common.allPartners}</Link></Button>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {(brokers || []).slice(0, 3).map((b) => <BrokerCard key={b.id} broker={b} />)}
          </div>
        </div>
      </section>

      <section className="bg-canvas py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading overline={L.platforms.overline} title={L.platforms.title} description={L.platforms.desc} />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {L.platforms.items.map((p, i) => {
              const Icon = PLATFORM_ICONS[i];
              return (
                <div key={i} className="rounded-xl border border-hair bg-white p-6 shadow-sm">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-navy text-gold"><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-ink">{p[0]}</h3>
                  <p className="mt-2 text-sm text-subtle">{p[1]}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-10"><Button asChild className="bg-finblue hover:bg-finblueDark"><Link to="/trading-platforms">{L.platforms.cta}</Link></Button></div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-tight">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading overline={L.insights.overline} title={L.insights.title} />
            <Button asChild variant="outline" data-testid="home-view-insights"><Link to="/market-analysis">{L.insights.viewAll}</Link></Button>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {(articles?.items || []).map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        </div>
      </section>

      <section className="bg-canvas py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading overline={L.education.overline} title={L.education.title} description={L.education.desc} />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {(education || []).slice(0, 6).map((e) => <EducationCard key={e.id} lesson={e} />)}
          </div>
          <div className="mt-10"><Button asChild className="bg-finblue hover:bg-finblueDark"><Link to="/education">{L.education.cta}</Link></Button></div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy2 py-20 lg:py-28">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="container-tight relative grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="overline mb-3 text-gold">{L.partnership.overline}</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white">{L.partnership.title}</h2>
            <p className="mt-4 text-lg leading-relaxed text-slate-300">{L.partnership.desc}</p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-300">
              {L.partnership.benefits.map((b) => <li key={b} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{b}</li>)}
            </ul>
            <Button asChild size="lg" className="mt-8 bg-finblue hover:bg-finblueDark" data-testid="home-become-partner"><Link to="/become-a-partner">{L.common.becomePartner}</Link></Button>
          </div>
          <div className="relative"><img src={IMAGES.partnership} alt="Professional partnership" className="rounded-2xl border border-white/10 object-cover shadow-2xl" /></div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-tight grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <SectionHeading overline={L.faqSection.overline} title={L.faqSection.title} description={L.faqSection.desc} />
          <FAQAccordion items={isAr ? HOME_FAQ_AR : HOME_FAQ} idPrefix="home-faq" />
        </div>
      </section>

      <CTASection />

      <section className="py-20"><div className="container-tight"><Newsletter variant="light" /></div></section>
    </>
  );
}
